/**
 * Contrato del módulo Record (docs/SHARE-SCREEN-SPEC.md §4.12 y §5.3). Espejo EXACTO de lo que
 * devuelve /api/recordings/*: si el backend cambia un campo se cambia acá y el compilador señala a
 * quién le duele.
 */

export type RecordingStatus = 'uploading' | 'uploaded' | 'processing' | 'ready' | 'failed' | 'deleted';
export type RecordingSurface = 'monitor' | 'window' | 'browser';

export type RecordingDTO = {
  id: number;
  title: string;
  status: RecordingStatus;
  owner: { id: string; name: string };
  mimeRaw: string;
  transcoded: boolean;
  durationMs: number | null;
  width: number | null;
  height: number | null;
  sizeBytes: number | null;
  hasMic: boolean;
  hasSystemAudio: boolean;
  hasCamera: boolean;
  surface: RecordingSurface | null;
  claimId: number | null;
  /** El claim como lo nombra el resto del app (claim # y homeowner); null sin claim. */
  claim: { id: number; claimNumber: string | null; homeownerName: string | null } | null;
  claimFileId: number | null;
  createdAt: string;
  uploadedAt: string | null;
  readyAt: string | null;
  expiresAt: string | null;
  keepAt: string | null;
  sentTo: number;
  seenBy: number;
  watchedToEnd: number;
  error: string | null;
};

/** POST /api/recordings */
export type CreateRecordingResponse = { id: number; uploadId: string; partSize: number; expiresAt: string };

/** POST /api/recordings/:id/parts */
export type PartUrl = { partNumber: number; url: string; expiresAt: string };
export type PartsResponse = { urls: PartUrl[] };

/** GET /api/recordings/:id */
export type RecordingView = {
  recording: RecordingDTO;
  mediaUrl: string | null;
  posterUrl: string | null;
  canDelete: boolean;
  isOwner: boolean;
};

/** POST /api/recordings/:id/send */
export type SendResponse = { recording: RecordingDTO; sentTo: number };

/** GET /api/recordings/mine */
export type MineResponse = { rows: RecordingDTO[] };

/**
 * GET /api/recordings/received — las que ME mandaron (soy destinatario), cualquier estado, más
 * nuevas arriba. GET /api/recordings/inbox — subconjunto para el banner: `ready`, sin ver y sin
 * descartar. Las DOS son de solo lectura (LEY escribir-al-leer). Contrato pendiente del backend
 * (ver notas del handoff): mismo `RecordingDTO` que el resto.
 */
export type ReceivedResponse = { rows: RecordingDTO[] };
/**
 * `rows` = lo que se pinta en el banner (ready, sin ver y sin descartar). `unseenCount` = TODO lo
 * recibido sin ver (ignora el descarte): es el badge del nav de Screen recording. Cerrar el banner
 * NO baja el badge — todavía no lo viste. Son dos números distintos a propósito.
 */
export type InboxResponse = { rows: RecordingDTO[]; unseenCount: number };

/** "#REC-1 · Ana Nativa" (o "#<id>" si no hay nada mejor): cómo se nombra un claim en el resto del app. */
export function claimLabel(c: RecordingDTO['claim'] | { id: number | string; claimNumber?: string | null; homeownerName?: string | null } | null | undefined): string {
  if (!c) return '';
  const parts = [c.claimNumber ? `#${c.claimNumber}` : null, c.homeownerName || null].filter(Boolean);
  return parts.length ? parts.join(' · ') : `#${c.id}`;
}

/** Estados terminales de una grabación (ya no cambian solos). */
export const TERMINAL_STATUSES: ReadonlySet<RecordingStatus> = new Set(['ready', 'failed', 'deleted']);
